import React from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';

export interface MineSubsidenceProperties {
  id: string | number;
  facility_name: string | null;
  operator: string | null;
  permit_id: string | null;
  seam: string | null;
}

interface Props {
  zone: MineSubsidenceProperties;
  onClose: () => void;
}

export const MineSubsidenceDetail: React.FC<Props> = ({ zone, onClose }) => {
  const { theme } = useTheme();
  const color = '#7c2d12';

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={18} color={color} />
          <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>Underground Mine Zone</h2>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        <div style={{
          padding: '10px 14px', marginBottom: '16px', borderRadius: '8px',
          background: color + '15', border: `1px solid ${color}30`,
        }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color, marginBottom: '6px' }}>
            Historic coal mine footprint
          </div>
          <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.5 }}>
            Parcels inside this polygon sit above a mapped underground mine (longwall or room-and-pillar).
            Static settlement continues for decades after abandonment; active subsidence can occur over
            longwall panels. Standard DC foundation spec is not safe without geotechnical verification.
          </div>
        </div>

        <Section title="Mine Details">
          <Field label="Facility" value={zone.facility_name} />
          <Field label="Operator" value={zone.operator} />
          <Field label="Permit ID" value={zone.permit_id} />
          <Field label="Coal Seam" value={zone.seam} />
        </Section>

        <Section title="DC Foundation Impact">
          <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.7 }}>
            <ImpactItem
              label="Geotechnical Survey"
              detail="Pre-design subsurface investigation required. Borings to below mine workings, seismic reflection or ground-penetrating radar. Typical cost: $80K–$250K for a 2,500-ac site."
              theme={theme}
            />
            <ImpactItem
              label="Foundation Design"
              detail="Shallow mat foundations typically ruled out. Options: grouted void fill + rigid slab, deep piers to sound rock below workings, or siting around the mine footprint. Added cost: $200K–$2M per building, depending on depth-to-mine and void volume."
              theme={theme}
            />
            <ImpactItem
              label="Insurance"
              detail="Mine subsidence is excluded from most property policies. Separate mine subsidence insurance available in WV via the state pool, but commercial limits are often below DC replacement value."
              theme={theme}
            />
            <ImpactItem
              label="Siting Alternative"
              detail="If mine footprint covers <40% of assembled acreage, relocate pads to non-undermined areas. Confirm with WVGES coal bed maps before site layout."
              theme={theme}
            />
          </div>
        </Section>

        <Section title="Data Source">
          <div style={{ fontSize: '11px', color: theme.textTertiary, lineHeight: 1.6 }}>
            WVDEP TAGIS · mining_reclamation MapServer layer 10 (underground mining limits).
            Updated per permit-holder submissions.
          </div>
        </Section>
      </div>
    </div>
  );
};

const ImpactItem: React.FC<{ label: string; detail: string; theme: ReturnType<typeof useTheme>['theme'] }> = ({ label, detail, theme }) => (
  <div style={{ marginBottom: '10px', paddingLeft: '10px', borderLeft: `2px solid ${theme.borderLight}` }}>
    <div style={{ fontSize: '12px', fontWeight: 600, color: theme.text, marginBottom: '2px' }}>{label}</div>
    <div style={{ fontSize: '11px', color: theme.textSecondary, lineHeight: 1.5 }}>{detail}</div>
  </div>
);
